import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinByInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;
        const run = async () => {
            try {
                await axios.get(`${API}/invites/${token}`);
            } catch (err) {
                setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "This invite link is not valid.");
                return;
            }
            try {
                await axios.get(`${API}/auth/me`, { withCredentials: true });
            } catch {
                sessionStorage.setItem("pending_invite", token);
                navigate("/auth", { replace: true });
                return;
            }
            try {
                await axios.post(`${API}/invites/${token}/accept`, {}, { withCredentials: true });
                navigate("/onboarding?photo=1", { replace: true });
            } catch (err) {
                setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not join this family.");
            }
        };
        run();
    }, [token, navigate]);

    return (
        <main data-testid="join-invite-page" className="flex min-h-screen items-center justify-center bg-cream px-6 text-navy">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                className="w-full max-w-md rounded-3xl border-2 border-warmgray bg-ivory p-10 text-center shadow-xl shadow-navy/5"
            >
                <img src="/logo.png" alt="Ago GenApp" className="mx-auto h-11 w-auto" />
                {error ? (
                    <>
                        <h1 className="mt-6 font-display text-2xl" data-testid="join-invite-error">This invite can't be used.</h1>
                        <p className="mt-2 text-sm leading-relaxed text-navy">{error}</p>
                        <Link
                            to="/dashboard"
                            data-testid="join-invite-dashboard-link"
                            className="btn-gradient mt-6 inline-block rounded-full px-7 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            Go to your archive
                        </Link>
                    </>
                ) : (
                    <>
                        <Loader2 size={28} className="mx-auto mt-8 animate-spin text-lavender" />
                        <p data-testid="join-invite-loading" className="mt-4 text-sm font-medium text-navy">
                            Opening your family invitation…
                        </p>
                    </>
                )}
            </motion.div>
        </main>
    );
}
